<template lang="pug">
#emergency-transport-patient-setting.box(v-cloak)
  .box-header.register-header.with-border
    .d-flex.justify-content-start.align-items-center
      .d-flex.justify-content-start.flex-column
        .title
          span 救急搬送の設定

  .box-body.register-body
    FormInputRadio(
      v-model="emergencyTransportRole"
      key-name="使用する救急搬送機能"
      name="emergency-transport-role"
      :options="[{ value: 'SENDER', label: '送信機能を使用する' }, { value: 'RECEIVER', label: '受信機能を使用する' }]"
      :validation-rules="{ required: true }"
      required
    )

    //- エラーメッセージ
    .register-row(v-show="errorMessage")
      .register-group
        .register-value
          .error-message {{ errorMessage }}

  .box-footer.register-footer
    ValidationAlert(v-model="showValidationAlert")
    .buttons.m-0.d-flex.flex-row.justify-content-end
      button.btn.update-btn(@click="onUpdate" type="button") 更新
      button.btn.cancel-btn(@click="gotoList" type="button") キャンセル

  //- モーダル
  OkCancelModal(v-model="showUpdateModal" title="救急搬送の設定を更新しますか？" @ok="updateSetting")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoList")

  //- ページ全体ローディング
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";

import FacilitiesService, { EmergencyTransportRole, FacilityGetModel, FacilityPatchModel } from "@/commons/services/Facilities";

import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";

import FormInputRadio from "@/pc/components/forms/FormInputRadio.vue";

/**
 * 救急搬送設定画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    ValidationAlert,
    OkCancelModal,
    OkModal,
    FormInputRadio
  },
  $_veeValidate: { validator: "new" }
})
export default class EmergencyTransportPatientSetting extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  emergencyTransportRole: EmergencyTransportRole | null = null;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      this.emergencyTransportRole = userStore.facility.emergencyTransportRole || EmergencyTransportRole.SENDER;
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### 設定更新処理
   */
  public async updateSetting(): Promise<void> {
    if (!this.emergencyTransportRole) return;
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: FacilityPatchModel = {
        name: userStore.facility.name ? this.$stringUtil.convertYen(userStore.facility.name) : "",
        tel: userStore.facility.tel ? userStore.facility.tel : "",
        url: userStore.facility.url ? this.$stringUtil.convertYen(userStore.facility.url) : "",
        emergencyTransportRole: this.emergencyTransportRole,
        updated: userStore.facility.updated
      };
      const updatedFacility: FacilityGetModel = await this.facilitiesService.patch(userStore, requestBody);

      if (updatedFacility) {
        userStore.updateFacility({ facility: updatedFacility });
      } else {
        const facility = { ...userStore.facility, emergencyTransportRole: this.emergencyTransportRole };
        userStore.updateFacility({ facility });
      }

      this.showUpdateSuccessModal = true;
    } catch (error) {
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode === 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      } else {
        errorHandle(error, this);
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 一覧画面へ遷移する
   */
  public gotoList(): void {
    this.$router.push(PcUrl.OtherOfficeEmergencyTransports);
    return;
  }
}
</script>

<style lang="scss" scoped>
#emergency-transport-patient-setting {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
